import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Progress,
  Radio,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import {
  DownloadOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { saveAs } from 'file-saver';
import './index.less';

const { Title, Text, Paragraph } = Typography;

/**
 * ★ E96：二维码 BASE 固定默认值 = 生产环境真正公开的地址。
 * 不读环境变量、不探测服务端，直接作为 input 默认值；可用作前缀拼 `<base>/<hash>`。
 */
const DEFAULT_BASE_URL = 'https://ds2.cn/tp/';
const API = '/api/v1/top-power';
const BOM = new Uint8Array([0xef, 0xbb, 0xbf]);

interface CharacterEntry {
  name: string;
  label?: string;
  /** ★ E98 卡牌编号（BC330-<三位序号>）；生成时文件名用它 */
  code?: string;
}

interface BatchProgress {
  currentTypeIndex: number;
  totalTypes: number;
  status: 'idle' | 'running' | 'completed' | 'error';
  completedTypes: string[];
  failedTypes: { code: string; error: string }[];
}

const getToken = (): string | null => localStorage.getItem('access_token');

/** 统一带 JWT 的请求；401 跳登录 */
const api = async (url: string, options: RequestInit = {}): Promise<any> => {
  const token = getToken();
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ***}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (res.status === 401) {
    window.location.href = '/#/login';
    throw new Error('未登录或登录已过期，请重新登录');
  }
  const text = await res.text();
  let json: any = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    throw new Error(`响应不是合法 JSON（HTTP ${res.status}）`);
  }
  if (!res.ok) {
    throw new Error((json && json.message) || `请求失败（HTTP ${res.status}）`);
  }
  return json;
};

const downloadTxt = (lines: string[], fileName: string) => {
  const content = lines.join('\r\n') + (lines.length ? '\r\n' : '');
  saveAs(new Blob([BOM, content], { type: 'text/plain;charset=utf-8' }), fileName);
  message.success(`已下载 ${fileName}`);
};

const fetchWithRetry = async (
  url: string,
  options: RequestInit,
  maxRetries = 3,
  retryDelay = 1000,
): Promise<Response> => {
  let lastError: Error | null = null;
  for (let i = 0; i <= maxRetries; i++) {
    try {
      const res = await fetch(url, options);
      if (res.ok) return res;
      if (res.status >= 400 && res.status < 500) return res;
      throw new Error(`Server error: ${res.status}`);
    } catch (err: any) {
      lastError = err;
      if (i === maxRetries) break;
      await new Promise(r => setTimeout(r, retryDelay * Math.pow(2, i)));
    }
  }
  throw lastError || new Error('请求失败');
};

const TopPowerAdmin: React.FC = () => {
  const [characters, setCharacters] = useState<CharacterEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [charactersHint, setCharactersHint] = useState<string>('');

  // ★ 二维码 BASE：input 默认值固定为生产 BASE，不读环境变量
  const [baseUrl, setBaseUrl] = useState<string>(DEFAULT_BASE_URL);

  // 单个生成
  const [generating, setGenerating] = useState(false);
  const [selected, setSelected] = useState<CharacterEntry | null>(null);
  const [singleVisible, setSingleVisible] = useState(false);
  const [singleForm] = Form.useForm();

  // 批量生成
  const [batchVisible, setBatchVisible] = useState(false);
  const [batchForm] = Form.useForm();
  const [batchProgress, setBatchProgress] = useState<BatchProgress>({
    currentTypeIndex: 0,
    totalTypes: 0,
    status: 'idle',
    completedTypes: [],
    failedTypes: [],
  });

  /** 拼完整二维码链接：${BASE}/<hash> */
  const buildUrl = (hash: string): string =>
    `${(baseUrl || DEFAULT_BASE_URL).trim().replace(/\/+$/, '')}/${hash}`;

  const fetchCharacters = async () => {
    setLoading(true);
    try {
      const data = await api(`${API}/admin/characters`);
      if (data && data.success) {
        const list: CharacterEntry[] = (data.data || []).filter(
          (e: any) => e && typeof e.name === 'string',
        );
        setCharacters(list);
        setCharactersHint(list.length ? '' : data.message || '暂无可用角色');
      } else {
        setCharacters([]);
        setCharactersHint((data && data.message) || '获取角色清单失败');
      }
    } catch (e: any) {
      setCharacters([]);
      setCharactersHint(e.message || '获取角色清单失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCharacters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** 单个角色生成 → 直接下载该角色 txt（每行一个完整二维码链接） */
  const handleSingleGenerate = async (values: any) => {
    const target = selected;
    if (!target) return;
    const quantity = Number(values.quantity);
    setGenerating(true);
    try {
      const data = await api(
        `${API}/admin/characters/${encodeURIComponent(target.name)}/generate`,
        { method: 'POST', body: JSON.stringify({ quantity }) },
      );
      if (data && data.success) {
        const hashes: string[] = (data.data && data.data.hashes) || [];
        const urls = hashes.map(buildUrl);
        // ★ E98：文件名 = 卡牌编号（BC330-<三位序号>）
        const code = target.code || target.label || target.name;
        downloadTxt(urls, `${code}.txt`);
        message.success(`成功生成 ${hashes.length} 个哈希（${target.label || target.name}）`);
        setSingleVisible(false);
        singleForm.resetFields();
      } else {
        message.error((data && data.message) || '生成哈希失败');
      }
    } catch (e: any) {
      message.error(e.message || '生成哈希失败');
    } finally {
      setGenerating(false);
    }
  };

  const downloadAsZip = async (files: { name: string; content: string }[]) => {
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();
    files.forEach(f => zip.file(`${f.name}.txt`, `\ufeff${f.content}`, { binary: false }));
    const blob = await zip.generateAsync({ type: 'blob' });
    const fileName = `top_power_batch_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.zip`;
    saveAs(blob, fileName);
    message.success(`已打包 ${files.length} 个文件：${fileName}`);
    return fileName;
  };

  /** ★ 批量生成：为全部角色各生成 N 个哈希 → 打包 zip 下载 */
  const handleBatchGenerate = async (values: any) => {
    const quantity = Number(values.quantity);
    const downloadType: 'zip' | 'batch' = values.downloadType || 'zip';
    setBatchProgress({
      currentTypeIndex: 0,
      totalTypes: characters.length,
      status: 'running',
      completedTypes: [],
      failedTypes: [],
    });
    setGenerating(true);
    try {
      const allFiles: { name: string; content: string }[] = [];
      for (let i = 0; i < characters.length; i++) {
        const c = characters[i];
        setBatchProgress(p => ({ ...p, currentTypeIndex: i + 1 }));
        try {
          const res = await fetchWithRetry(
            `${API}/admin/characters/${encodeURIComponent(c.name)}/generate`,
            { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ quantity }) },
            3,
            1000,
          );
          const data = await res.json();
          if (data.success && data.data?.hashes) {
            const content = data.data.hashes.map((h: string) => buildUrl(h)).join('\r\n') + '\r\n';
            // ★ E98：文件名 = 卡牌编号（BC330-<三位序号>）
            const fileName = c.code || c.label || c.name;
            allFiles.push({ name: fileName, content });
            setBatchProgress(p => ({ ...p, completedTypes: [...p.completedTypes, c.name] }));
            if (downloadType === 'batch') {
              saveAs(new Blob([BOM, content], { type: 'text/plain;charset=utf-8' }), `${fileName}.txt`);
              await new Promise(r => setTimeout(r, 500));
            }
          } else {
            throw new Error(data.message || '生成失败');
          }
        } catch (err: any) {
          setBatchProgress(p => ({
            ...p,
            failedTypes: [...p.failedTypes, { code: c.name, error: err.message || '未知错误' }],
          }));
        }
      }
      if (downloadType === 'zip' && allFiles.length > 0) {
        await downloadAsZip(allFiles);
      }
      setBatchProgress(p => ({ ...p, status: p.failedTypes.length ? 'error' : 'completed' }));
      message.success(`批量生成完成：成功 ${allFiles.length} 个角色`);
    } catch (err) {
      message.error('批量生成失败');
      setBatchProgress(p => ({ ...p, status: 'error' }));
    } finally {
      setGenerating(false);
    }
  };

  const openSingle = (record: CharacterEntry) => {
    setSelected(record);
    setSingleVisible(true);
  };

  const resetBatch = () => {
    setBatchVisible(false);
    batchForm.resetFields();
    setBatchProgress(p => ({ ...p, status: 'idle' }));
  };

  const columns = [
    {
      title: '编号',
      dataIndex: 'code',
      key: 'code',
      width: 150,
      render: (v: string) => <Text code>{v || '—'}</Text>,
    },
    {
      title: '角色',
      dataIndex: 'label',
      key: 'label',
      ellipsis: true,
      render: (v: string, row: CharacterEntry) => (
        <Space size={8}>
          <Text strong>{v || row.name}</Text>
          {v && v !== row.name && <Text type="secondary" style={{ fontSize: 12 }}>{row.name}</Text>}
        </Space>
      ),
    },
    {
      title: '编码',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      ellipsis: true,
      render: (v: string) => <Text type="secondary">{v}</Text>,
    },
    {
      title: '操作',
      key: 'action',
      width: 130,
      fixed: 'right' as const,
      render: (_: any, row: CharacterEntry) => (
        <Button
          type="link"
          size="small"
          icon={<ThunderboltOutlined />}
          loading={generating && selected?.name === row.name && singleVisible}
          onClick={() => openSingle(row)}
        >
          生成
        </Button>
      ),
    },
  ];

  return (
    <div className="top-power-admin">
      <Card
        bordered={false}
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>
              战力天花板 · 角色哈希生成
            </Title>
          </Space>
        }
        extra={
          <Space>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              disabled={!characters.length}
              onClick={() => setBatchVisible(true)}
            >
              批量生成
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetchCharacters}>
              刷新
            </Button>
          </Space>
        }
      >
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          为登场角色生成哈希：单个角色点行内「生成」；批量「批量生成」会为全部角色各生成指定数量并打包成
          <Text code>.zip</Text> 下载。二维码链接 = <Text code>{`${(baseUrl || DEFAULT_BASE_URL).trim().replace(/\/+$/, '')}/<hash>`}</Text>。
          生成会真实写入生产库，请按需生成。
        </Paragraph>

        <Space size={8} wrap style={{ marginBottom: 12 }}>
          <Text strong>二维码 BASE</Text>
          <Input
            value={baseUrl}
            onChange={e => setBaseUrl(e.target.value)}
            style={{ width: 320 }}
            placeholder={DEFAULT_BASE_URL}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            样例：<Text code>{buildUrl('a1b2c3d4')}</Text>
          </Text>
        </Space>

        {charactersHint && !loading && (
          <Alert style={{ marginBottom: 12 }} type="warning" showIcon message={charactersHint} />
        )}

        <Table<CharacterEntry>
          rowKey="name"
          size="small"
          loading={loading}
          columns={columns as any}
          dataSource={characters}
          locale={{ emptyText: <Empty description="暂无登场角色" /> }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: t => `共 ${t} 个角色`,
          }}
          scroll={{ x: 700 }}
        />
      </Card>

      {/* 单个生成 Modal */}
      <Modal
        title={`生成哈希 - ${selected?.label || selected?.name || ''}`}
        open={singleVisible}
        onOk={() => singleForm.submit()}
        confirmLoading={generating}
        onCancel={() => {
          setSingleVisible(false);
          singleForm.resetFields();
        }}
      >
        <Form form={singleForm} layout="vertical" initialValues={{ quantity: 100 }} onFinish={handleSingleGenerate}>
          <Form.Item
            name="quantity"
            label="生成数量"
            rules={[
              { required: true, message: '请输入数量' },
              { type: 'number', min: 1, max: 100000, message: '数量需在 1~100000' },
            ]}
          >
            <InputNumber min={1} max={100000} precision={0} style={{ width: '100%' }} />
          </Form.Item>
          <Text type="secondary">
            生成后自动下载 <Text code>{selected?.code || 'BC330-XXX'}.txt</Text>（文件名 = 卡牌编号；每行一个二维码链接：{`${(baseUrl || DEFAULT_BASE_URL).trim().replace(/\/+$/, '')}/<hash>`}）
          </Text>
        </Form>
      </Modal>

      {/* ★ 批量生成 Modal */}
      <Modal
        title="批量生成全部角色哈希"
        open={batchVisible}
        footer={null}
        width={600}
        onCancel={resetBatch}
      >
        <Form form={batchForm} layout="vertical" onFinish={handleBatchGenerate}>
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">
              将为全部角色（共 {characters.length} 个）各生成指定数量的哈希；每个 <Text code>.txt</Text> 以卡牌编号命名（如 <Text code>BC330-001.txt</Text>）
            </Text>
          </div>
          <Form.Item
            name="quantity"
            label="每个角色生成数量"
            initialValue={100}
            rules={[
              { required: true, message: '请输入数量' },
              { type: 'number', min: 1, max: 100000, message: '数量需在 1~100000' },
            ]}
          >
            <InputNumber min={1} max={100000} precision={0} style={{ width: '100%' }} disabled={generating} />
          </Form.Item>
          <Form.Item name="downloadType" label="下载方式" initialValue="zip" rules={[{ required: true }]}>
            <Radio.Group disabled={generating}>
              <Radio value="zip">ZIP 打包</Radio>
              <Radio value="batch">即时下载</Radio>
            </Radio.Group>
          </Form.Item>
          {batchProgress.status !== 'idle' && (
            <div className="progress-section">
              <Text strong>生成进度</Text>
              <div style={{ marginBottom: 8 }}>
                {batchProgress.status === 'running' && <Text>正在处理：{batchProgress.currentTypeIndex}/{batchProgress.totalTypes}</Text>}
                {batchProgress.status === 'completed' && <Text type="success">✓ 完成</Text>}
                {batchProgress.status === 'error' && <Text type="danger">✗ 部分失败</Text>}
              </div>
              <Progress
                percent={
                  batchProgress.totalTypes > 0
                    ? Math.round((batchProgress.currentTypeIndex / batchProgress.totalTypes) * 100)
                    : 0
                }
                status={
                  batchProgress.status === 'completed'
                    ? 'success'
                    : batchProgress.status === 'error'
                    ? 'exception'
                    : 'active'
                }
                format={() => `${batchProgress.currentTypeIndex}/${batchProgress.totalTypes}`}
              />
              <div style={{ marginTop: 12, fontSize: 12 }}>
                <Text type="success">已完成：{batchProgress.completedTypes.length} 个</Text>
                {batchProgress.failedTypes.length > 0 && (
                  <>
                    <br />
                    <Text type="danger">失败：{batchProgress.failedTypes.length} 个</Text>
                    <div className="failed-list">
                      {batchProgress.failedTypes.map(f => (
                        <div className="failed-item" key={f.code}>
                          <Text code>{f.code}</Text>：{f.error}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={generating}
                disabled={batchProgress.status === 'running'}
                icon={<DownloadOutlined />}
              >
                {generating ? '生成中…' : '开始批量生成'}
              </Button>
              <Button onClick={resetBatch}>关闭</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TopPowerAdmin;
